import React from "react";
import { useMutation } from "@apollo/client";
import {
  CreateManagerMutation,
  CreateManagerMutationVariables,
  EmployerPermissions,
  UpdateEmployerRewardBudgetMutation,
  UpdateEmployerRewardBudgetMutationVariables
} from "generated/graphql";
import { useRouter } from "Hooks/useRouter";
import { successToast } from "Modules/DPToast";
import { handleValidationErrors } from "Modules/HandleValidationErrors";
import moneyToCents from "Shared/money";
import mutations from "Shared/mutations";
import queries from "Shared/queries";
import { AmplitudeProvider } from "Shared/Analytics/Amplitude";
import { AmplitudeEventType } from "Shared/Analytics/Amplitude/Types/AmplitudeEventType";
import { permissionLabels } from "Shared/types/Admins";
import { OperatingManagerFormFields } from "Shared/types/OperatingManager";
import { ConfirmationForm } from "./ConfirmationForm/ConfirmationForm";
import { queryParamsToProps } from "../../../services/queryParams";
import { ROWS_PER_PAGE } from "Components/Pages/RewardsSetup/services/useOperatingManagersHook";

export const useConfirmationStep = (formFields: OperatingManagerFormFields, existingEmployerId: string | undefined | null) => {
  const [createManagerFn] = useMutation<CreateManagerMutation, CreateManagerMutationVariables>(
    mutations.CREATE_MANAGER
  );
  const { location } = useRouter();
  const { page } = queryParamsToProps(location.search);

  const [updateRewardBudgetFn] = useMutation<UpdateEmployerRewardBudgetMutation, UpdateEmployerRewardBudgetMutationVariables>(
    mutations.UPDATE_REWARD_BUDGET, {
      refetchQueries: [
        {
          query: queries.GET_OPERATING_MANAGERS,
          variables: {
            page,
            perPage: ROWS_PER_PAGE,
          }
        },
      ]
    }
  );

  const onComplete = async () => {
    let employerId: string = existingEmployerId || "";

    try {
      if (!existingEmployerId) {
        const manager = await createManagerFn({
          variables: {
            createManagerInput: {
              firstName: formFields.firstName,
              lastName: formFields.lastName,
              email: formFields.email,
              permissions: formFields.permissions?.value as EmployerPermissions,
              locationIds: formFields.locations ? formFields.locations.map(location => location.value) : undefined,
              phoneNumber: formFields.phoneNumber.replace(/([\s-)(])/gi, ""),
            },
          },
        });
        employerId = manager.data!.createManager.managers[0].employerId;
      }
      await updateRewardBudgetFn({
        variables: {
          updateEmployerRewardBudgetInput: {
            expectedApproverIds: formFields.secondaryApprovers?.map(approver => approver.value),
            employerId,
            totalAmountCents: moneyToCents(formFields.budget),
          },
        },
      });
      successToast("Operation Manager successfully created.", false);
      AmplitudeProvider.logEvent({
        type: AmplitudeEventType.OperatingManagerCreated,
        payload: {
          accessLevel:
            permissionLabels[
              formFields.permissions?.value as EmployerPermissions
            ],
        },
      });
    } catch (error) {
      handleValidationErrors(error);
    }

    return {};
  };

  const StepContent = (
    <ConfirmationForm formFields={formFields} />
  );

  return {
    label: "Confirmation",
    content: StepContent,
    onComplete,
  };
};
